import { useState, useEffect } from "react"
import { useData } from "../contexts/DataContext"
import Navigation from "../components/Navigation"
import Header from "../components/Header"
import NotificationToaster from "../components/NotificationToaster"
import Login from "../components/Login"
import LogoutConfirmModal from "../components/LogoutConfirmModal"
import useLogout from "../hooks/useLogout"
import { 
  globalPropertiesAPI, 
  propertyGroupsAPI, 
  propertySetsAPI, 
  reportsAPI 
} from "../services/componentServices"
import '../styles.css';
import '../button-styles.css';

/* ========================================================================================
   SHARED UI — Unified Button System + Icons
   ======================================================================================== */
const BtnPrimary = ({ onClick, disabled, children, className = "", color = "purple" }) => {
  const colors = {
    purple: "bg-purple-600 hover:bg-purple-700 focus:ring-purple-500",
    orange: "bg-orange-600 hover:bg-orange-700 focus:ring-orange-500",
    indigo: "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500",
    emerald: "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500",
    blue: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`h-10 px-4 py-2 rounded-lg font-medium text-sm text-white transition-all flex items-center gap-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${colors[color] || colors.purple} ${className}`}>
      {children}
    </button>
  );
};
const BtnSecondary = ({ onClick, disabled, children, className = "" }) => (
  <button onClick={onClick} disabled={disabled}
    className={`h-10 px-4 py-2 rounded-lg font-medium text-sm text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-all flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
    {children}
  </button>
);
const BtnDanger = ({ onClick, disabled, children, className = "" }) => (
  <button onClick={onClick} disabled={disabled}
    className={`h-10 px-4 py-2 rounded-lg font-medium text-sm text-white bg-red-600 hover:bg-red-700 transition-all flex items-center gap-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
    {children}
  </button>
);

const IconPlus = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>);
const IconDownload = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>);
const IconTrash = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>);
const IconEdit = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>);
const IconCheck = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>);
const IconX = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>);
const IconSearch = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>);
const IconSpinner = ({ className = "w-4 h-4" }) => (<svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>);
const IconCopy = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>);
const IconChevronRight = ({ className = "w-4 h-4" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>);

const PropertiesPage = ({
  currentUser,
  onLogout,
  onShowUserManagement,
  onTabChange,
  activeTab,
  sidebarOpen,
  onToggleSidebar,
}) => {
  // Normalize common shorthand to standard property name used across the tool
  const normalizePropertyName = (name) => {
    const s = String(name ?? "").trim();
    if (!s) return s;
    const u = s.toUpperCase();
    if (u === "SPG" || u === "SG" || u === "SPECIFICGRAVITY" || u === "SPECIFIC GRAVITY") return "Specific Gravity";
    return s;
  };


  const { savedProperties, deleteProperties, saveProperties } = useData()
  const [notifications, setNotifications] = useState([])
  const [activeSubTab, setActiveSubTab] = useState("global")
  const [showAddPropertyPopup, setShowAddPropertyPopup] = useState(false)
  const [newProperty, setNewProperty] = useState({
    property: "",
    uom: "",
    basis: "volume",
    linear: true,
    min: "",
    max: "",
    description: "",
    index_formula: "",
    reverse_formula: "",
  })
  const [editingIndex, setEditingIndex] = useState(null)

  const [selectedProperties, setSelectedProperties] = useState([])
  const [groupName, setGroupName] = useState("")
  const [savedGroups, setSavedGroups] = useState([])
  const [expandedGroups, setExpandedGroups] = useState({})

  const [savedPropertySets, setSavedPropertySets] = useState([])
  const [propertySetName, setPropertySetName] = useState("")
  const [propertySetMin, setPropertySetMin] = useState("")
  const [propertySetMax, setPropertySetMax] = useState("")
  const [expandedPropertySets, setExpandedPropertySets] = useState({})
  const [propertyLimits, setPropertyLimits] = useState({})

  const [globalProperties, setGlobalProperties] = useState([])

  const [allUniqueProperties, setAllUniqueProperties] = useState([])
  const [savedPropertiesFromApp, setSavedPropertiesFromApp] = useState([])
  const [isSavingToDB, setIsSavingToDB] = useState(false)
  const [hasAutoSaved, setHasAutoSaved] = useState(false) 

  const [showGradePropertyPopup, setShowGradePropertyPopup] = useState(false)
  const [savedPropertiesPage, setSavedPropertiesPage] = useState(1)
  const [globalPropertiesPage, setGlobalPropertiesPage] = useState(1)
  const [savedGroupsPage, setSavedGroupsPage] = useState(1)
  const [savedPropertySetsPage, setSavedPropertySetsPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [editingGroup, setEditingGroup] = useState(null)
  const [editingPropertySet, setEditingPropertySet] = useState(null)
  const [showEditGroupPopup, setShowEditGroupPopup] = useState(false)
  const [showEditPropertySetPopup, setShowEditPropertySetPopup] = useState(false)
  const [groupProperties, setGroupProperties] = useState([])
  const [propertySetProperties, setPropertySetProperties] = useState([])

  const [formErrors, setFormErrors] = useState({})

  // Loading states
  const [isLoading, setIsLoading] = useState({
    global: true,
    groups: true,
    propertySets: true,
    allProperties: true
  })

  const [editingPropertySetProperties, setEditingPropertySetProperties] = useState([])
  const [editingPropertySetMin, setEditingPropertySetMin] = useState("")
  const [editingPropertySetMax, setEditingPropertySetMax] = useState("")

  // Search, filter, sort state
  const [globalSearch, setGlobalSearch] = useState("")
  const [globalBasisFilter, setGlobalBasisFilter] = useState("all")
  const [globalTypeFilter, setGlobalTypeFilter] = useState("all")
  const [globalSort, setGlobalSort] = useState("name-asc")
  const [groupSearch, setGroupSearch] = useState("")
  const [groupFilter, setGroupFilter] = useState("all")
  const [groupSort, setGroupSort] = useState("name-asc")
  const [specSearch, setSpecSearch] = useState("")
  const [specFilter, setSpecFilter] = useState("all")
  const [specSort, setSpecSort] = useState("name-asc")
  const [isExporting, setIsExporting] = useState(false)

  // Use the common logout hook
  const {
    showLogoutConfirm,
    shouldRefresh,
    handleLogoutClick,
    handleConfirmLogout,
    handleCancelLogout,
    setShouldRefresh
  } = useLogout()

  const basisOptions = [
    { value: "volume", label: "Volume" },
    { value: "mass", label: "Mass/Weight" },
  ]

  const blendingTypeOptions = [
    { value: true, label: "Linear" },
    { value: false, label: "Non-Linear" },
  ]

  useEffect(() => {
    if (!currentUser && shouldRefresh) {
      window.location.reload();
    }
  }, [currentUser, shouldRefresh]);

  // API Functions using the service
  const fetchGlobalProperties = async () => {
    try {
      setIsLoading(prev => ({ ...prev, global: true }));
      const data = await globalPropertiesAPI.fetchAll();
      console.log('Fetched global properties:', data);
      setGlobalProperties(data.data || []);
    } catch (error) {
      console.error('Failed to fetch global properties:', error);
      addNotification('error', 'Failed to load global properties');
    } finally {
      setIsLoading(prev => ({ ...prev, global: false }));
    }
  };

  const createGlobalProperty = async (propertyData) => {
    try {
      const data = await globalPropertiesAPI.create(propertyData);
      return data;
    } catch (error) {
      console.error('Failed to create global property:', error);
      throw error;
    }
  };

  const updateGlobalProperty = async (propertyId, propertyData) => {
    try {
      const data = await globalPropertiesAPI.update(propertyId, propertyData);
      return data;
    } catch (error) {
      console.error('Failed to update global property:', error);
      throw error;
    }
  };

  const deleteGlobalProperty = async (propertyId) => {
    try {
      await globalPropertiesAPI.delete(propertyId);
    } catch (error) {
      console.error('Failed to delete global property:', error);
      throw error;
    }
  };

  // Property Groups API
  const fetchPropertyGroups = async () => {
    try {
      setIsLoading(prev => ({ ...prev, groups: true }));
      const data = await propertyGroupsAPI.fetchAll();
      console.log('Fetched groups:', data);
      setSavedGroups(data.data || []);
    } catch (error) {
      console.error('Failed to fetch property groups:', error);
      addNotification('error', 'Failed to load property groups');
    } finally {
      setIsLoading(prev => ({ ...prev, groups: false }));
    }
  };

  const createPropertyGroup = async (groupData) => {
    try {
      const data = await propertyGroupsAPI.create(groupData);
      return data;
    } catch (error) {
      console.error('Failed to create property group:', error);
      throw error;
    }
  };

  const updatePropertyGroup = async (groupId, groupData) => {
    try {
      const data = await propertyGroupsAPI.update(groupId, groupData);
      return data;
    } catch (error) {
      console.error('Failed to update property group:', error);
      throw error;
    }
  };

  const deletePropertyGroup = async (groupId) => {
    try {
      await propertyGroupsAPI.delete(groupId);
    } catch (error) {
      console.error('Failed to delete property group:', error);
      throw error;
    }
  };

  // Property Sets API
  const fetchPropertySets = async () => {
    try {
      setIsLoading(prev => ({ ...prev, propertySets: true }));
      const data = await propertySetsAPI.fetchAll();
      console.log('Fetched property sets:', data);
      setSavedPropertySets(data.data || []);
    } catch (error) {
      console.error('Failed to fetch property sets:', error);
      addNotification('error', 'Failed to load property sets');
    } finally {
      setIsLoading(prev => ({ ...prev, propertySets: false }));
    }
  };

  const createPropertySet = async (setData) => {
    try {
      const data = await propertySetsAPI.create(setData);
      return data;
    } catch (error) {
      console.error('Failed to create property set:', error);
      throw error;
    }
  };

  const updatePropertySet = async (setId, setData) => {
    try {
      const data = await propertySetsAPI.update(setId, setData);
      return data;
    } catch (error) {
      console.error('Failed to update property set:', error);
      throw error;
    }
  };

  const deletePropertySet = async (setId) => {
    try {
      await propertySetsAPI.delete(setId);
    } catch (error) {
      console.error('Failed to delete property set:', error);
      throw error;
    }
  };

  const fetchAllPropertiesReport = async () => {
    try {
      setIsLoading(prev => ({ ...prev, allProperties: true }));
      const data = await reportsAPI.fetchAllProperties();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch all properties report:', error);
      addNotification('error', 'Failed to load all properties report');
      return [];
    } finally {
      setIsLoading(prev => ({ ...prev, allProperties: false }));
    }
  };

  // const autoSaveAppPropertiesToDB = async (appProperties) => {
  //   if (!appProperties || appProperties.length === 0 || hasAutoSaved) return;

  //   setIsSavingToDB(true);
  //   try {
  //     const savedProperties = [];
      
  //     for (const property of appProperties) {
  //       try {
  //         const existingProperty = globalProperties.find(
  //           gp => gp.property === property.property && gp.uom === property.uom
  //         );

  //         if (!existingProperty) {
  //           const propertyData = {
  //             property: property.property,
  //             uom: property.uom || '',
  //             basis: property.basis || 'volume',
  //             linear: property.linear !== undefined ? property.linear : true,
  //             min: property.min || '',
  //             max: property.max || '',
  //             description: property.description || '',
  //             index_formula: property.index_formula || '',
  //             reverse_formula: property.reverse_formula || '',
  //           };

  //           const result = await createGlobalProperty(propertyData);
  //           if (result.success) {
  //             savedProperties.push(result.data);
  //             console.log(`Auto-saved property to DB: ${property.property}`);
  //           }
  //         }
  //       } catch (error) {
  //         console.error(`Failed to auto-save property ${property.property}:`, error);
  //       }
  //     }

  //     if (savedProperties.length > 0) {
  //       await fetchGlobalProperties();
  //       addNotification('success', `Auto-saved ${savedProperties.length} properties from App to database`);
        
  //       setHasAutoSaved(true);
        
  //       localStorage.removeItem('savedProperties');
  //       setSavedPropertiesFromApp([]);
  //     }

  //   } catch (error) {
  //     console.error('Failed to auto-save app properties to DB:', error);
  //     addNotification('error', 'Failed to auto-save properties to database');
  //   } finally {
  //     setIsSavingToDB(false);
  //   }
  // };

  // useEffect(() => {
  //   const loadAndAutoSaveAppProperties = async () => {
  //     try {
  //       const savedProps = localStorage.getItem('savedProperties');
  //       if (savedProps) {
  //         const parsedProps = JSON.parse(savedProps);
  //         console.log('Loaded properties from App.jsx:', parsedProps);
          
  //         const allProperties = [];
  //         parsedProps.forEach((propertySet, index) => {
  //           if (propertySet.properties && Array.isArray(propertySet.properties)) {
  //             propertySet.properties.forEach(prop => {
  //               allProperties.push({
  //                 ...prop,
  //                 source: 'app',
  //                 sourceName: propertySet.customName || `Saved Set ${index + 1}`,
  //                 timestamp: propertySet.timestamp,
  //                 id: `app-${index}-${prop.property}`
  //               });
  //             });
  //           }
  //         });
          
  //         setSavedPropertiesFromApp(allProperties);
  //         console.log('Processed properties from App:', allProperties);

  //         if (allProperties.length > 0 && !hasAutoSaved) {
  //           console.log('Auto-saving properties to MongoDB...');
  //           await autoSaveAppPropertiesToDB(allProperties);
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Error loading properties from App.jsx:', error);
  //     }
  //   };

  //   loadAndAutoSaveAppProperties();
  // }, [hasAutoSaved]); 

  const getCombinedProperties = () => {
    const combined = [...globalProperties];
    
    savedPropertiesFromApp.forEach(property => {
      const exists = combined.some(p => 
        p.property === property.property && 
        p.uom === property.uom
      );
      
      if (!exists) {
        combined.push({
          ...property,
          isFromApp: true,
          sourceType: 'app'
        });
      }
    });
    
    allUniqueProperties.forEach(property => {
      const exists = combined.some(p => 
        p.property === property.property && 
        p.uom === property.uom
      );
      
      if (!exists) {
        combined.push({
          ...property,
          isFromReport: true,
          sourceType: 'report'
        });
      }
    });
    
    return combined;
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchGlobalProperties(),
        fetchPropertyGroups(),
        fetchPropertySets()
      ]);
    };
    
    loadData();
  }, [])

  useEffect(() => {
    const loadAndMergeAllProperties = async () => {
      const properties = await fetchAllPropertiesReport();
      setAllUniqueProperties(properties);
    };
    loadAndMergeAllProperties();
  }, []);

  const addNotification = (type, message) => {
    const id = Date.now()
    setNotifications((prev) => [...prev, { id, type, message }])

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, 3000)
  }

  const validatePropertyForm = () => {
    const errors = {}

    if (!newProperty.property.trim()) {
      errors.property = "Property name is required"
    }

    if (!newProperty.uom.trim()) {
      errors.uom = "UOM name is required"
    }

    if (!newProperty.linear) {
      if (!newProperty.index_formula.trim()) {
        errors.index_formula = "Index formula is required for non-linear properties"
      }

      if (!newProperty.reverse_formula.trim()) {
        errors.reverse_formula = "Reverse formula is required for non-linear properties"
      }

      if (newProperty.index_formula.trim() && !newProperty.index_formula.includes('x')) {
        errors.index_formula = "Index formula must contain 'x' as variable"
      }

      if (newProperty.reverse_formula.trim() && !newProperty.reverse_formula.includes('x')) {
        errors.reverse_formula = "Reverse formula must contain 'x' as variable"
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const getPaginatedData = (data, currentPage, pageSize = itemsPerPage) => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return data.slice(startIndex, endIndex)
  }

  const getTotalPages = (data, pageSize = itemsPerPage) => {
    return Math.ceil(data.length / pageSize)
  }

  const handlePageChange = (page, setPageFunction) => {
    setPageFunction(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const renderPagination = (currentPage, totalPages, setPageFunction, dataLength) => {
    if (totalPages <= 1) return null

    const pageNumbers = []
    const maxVisiblePages = 5

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div></div>

        <div className="flex flex-col xs:flex-row items-center gap-2 w-full sm:w-auto">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value))
              setPageFunction(1)
            }}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full xs:w-auto"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1, setPageFunction)}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded border text-sm font-medium min-w-[70px] ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                  : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
              }`}
            >
              Previous
            </button>

            <div className="flex gap-1 overflow-x-auto max-w-[200px] sm:max-w-none">
              {startPage > 1 && (
                <>
                  <button
                    onClick={() => handlePageChange(1, setPageFunction)}
                    className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium min-w-[40px]"
                  >
                    1
                  </button>
                  {startPage > 2 && <span className="px-2 py-1.5 text-gray-500">...</span>}
                </>
              )}

              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page, setPageFunction)}
                  className={`px-3 py-1.5 rounded border text-sm font-medium min-w-[40px] ${
                    currentPage === page
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}

              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && <span className="px-2 py-1.5 text-gray-500">...</span>}
                  <button
                    onClick={() => handlePageChange(totalPages, setPageFunction)}
                    className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium min-w-[40px]"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1, setPageFunction)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 rounded border text-sm font-medium min-w-[70px] ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                  : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Loading Spinner Component
  const LoadingSpinner = ({ size = "medium", text = "Loading..." }) => {
    const sizeClasses = {
      small: "w-6 h-6",
      medium: "w-8 h-8",
      large: "w-12 h-12"
    };

    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className={`animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 ${sizeClasses[size]}`}></div>
        {text && <p className="mt-4 text-gray-600 text-sm">{text}</p>}
      </div>
    );
  };

  const handleUpdateGroupName = async (groupId, newName) => {
    try {
      const group = savedGroups.find(g => g._id === groupId);
      if (!group) return;
      
      const updatedGroup = {
        ...group,
        name: newName
      };
      
      await updatePropertyGroup(groupId, updatedGroup);
      await fetchPropertyGroups();
      addNotification("success", "Group name updated successfully");
    } catch (error) {
      console.error('Error updating group name:', error);
      addNotification("error", "Failed to update group name");
    }
  };

  const handleUpdatePropertySetName = async (setId, newName) => {
    try {
      const propertySet = savedPropertySets.find(s => s._id === setId);
      if (!propertySet) return;
      
      const updatedSet = {
        ...propertySet,
        name: newName
      };
      
      await updatePropertySet(setId, updatedSet);
      await fetchPropertySets();
      addNotification("success", "Property set name updated successfully");
    } catch (error) {
      console.error('Error updating property set name:', error);
      addNotification("error", "Failed to update property set name");
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setGroupProperties([...group.properties])
    setShowEditGroupPopup(true)
  }

  const handleSaveGroupEdit = async () => {
    if (!editingGroup) return

    try {
      const updatedGroup = {
        ...editingGroup,
        properties: groupProperties
      }

      await updatePropertyGroup(editingGroup._id, updatedGroup)
      await fetchPropertyGroups()
      
      setShowEditGroupPopup(false)
      setEditingGroup(null)
      setGroupProperties([])
      
      addNotification("success", "Group updated successfully")
    } catch (error) {
      console.error('Error updating group:', error)
      addNotification("error", "Failed to update group")
    }
  }

  const handleAddPropertyToGroup = (property) => {
    const exists = groupProperties.some(p => 
      p.property === property.property && p.uom === property.uom
    )
    
    if (!exists) {
      setGroupProperties(prev => [...prev, {
        ...property,
        source: 'global'
      }])
      addNotification("success", "Property added to group")
    } else {
      addNotification("info", "Property already exists in group")
    }
  }

  const handleRemovePropertyFromGroup = (index) => {
    setGroupProperties(prev => prev.filter((_, i) => i !== index))
    addNotification("info", "Property removed from group")
  }

  const handleEditPropertySet = (propertySet) => {
    setEditingPropertySet(propertySet)
    setEditingPropertySetProperties([...propertySet.properties.map(prop => ({ ...prop }))])
    setEditingPropertySetMin(propertySet.setMin || "")
    setEditingPropertySetMax(propertySet.setMax || "")
    setShowEditPropertySetPopup(true)
  }

  const handleSavePropertySetEdit = async () => {
    if (!editingPropertySet) return

    try {
      const updatedPropertySet = {
        ...editingPropertySet,
        properties: (editingPropertySetProperties || []).map(p => ({ ...p, property: normalizePropertyName(p.property) })),
        setMin: editingPropertySetMin,
        setMax: editingPropertySetMax
      }

      await updatePropertySet(editingPropertySet._id, updatedPropertySet)
      await fetchPropertySets()
      
      setShowEditPropertySetPopup(false)
      setEditingPropertySet(null)
      setEditingPropertySetProperties([])
      setEditingPropertySetMin("")
      setEditingPropertySetMax("")
      
      addNotification("success", "Property set updated successfully")
    } catch (error) {
      console.error('Error updating property set:', error)
      addNotification("error", "Failed to update property set")
    }
  }

  const handlePropertyMinMaxChange = (index, field, value) => {
    const updatedProperties = [...editingPropertySetProperties]
    updatedProperties[index] = {
      ...updatedProperties[index],
      [field]: value
    }
    setEditingPropertySetProperties(updatedProperties)
  }

  const handleAddPropertyToPropertySet = (property) => {
    const exists = editingPropertySetProperties.some(p => 
      p.property === property.property && p.uom === property.uom
    )
    
    if (!exists) {
      setEditingPropertySetProperties(prev => [...prev, {
        ...property,
         min: "", 
      max: "",
        source: 'global'
      }])
      addNotification("success", "Property added to property set")
    } else {
      addNotification("info", "Property already exists in property set")
    }
  }

  const handleRemovePropertyFromPropertySet = (index) => {
    setEditingPropertySetProperties(prev => prev.filter((_, i) => i !== index))
    addNotification("info", "Property removed from property set")
  }

  const handleSaveAsGroup = async () => {
    if (selectedProperties.length === 0) {
      addNotification("error", "Please select at least one property")
      return
    }

    if (!groupName.trim()) {
      addNotification("error", "Please enter a group name")
      return
    }

    try {
      const groupData = {
        name: groupName.trim(),
        properties: selectedProperties.map((p) => ({
          property: p.property,
          uom: p.uom,
          basis: p.basis,
          linear: p.linear,
          min: p.min,
          max: p.max,
          description: p.description,
          index_formula: p.index_formula,
          reverse_formula: p.reverse_formula,
          source: p.source,
          sourceId: p.sourceId,
        })),
      }

      console.log('Creating property group:', groupData);
      const result = await createPropertyGroup(groupData)
      
      await fetchPropertyGroups()
      
      const existingGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
      const updatedGroups = [...existingGroups, {
        ...groupData,
        _id: result.data?._id || `local-${Date.now()}`,
        id: result.data?._id || `local-${Date.now()}`,
        createdAt: new Date().toISOString()
      }];
      localStorage.setItem('propertyGroups', JSON.stringify(updatedGroups));
      
      window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
        detail: { groups: updatedGroups }
      }));
      
      setGroupName("")
      setSelectedProperties([])
      setShowGradePropertyPopup(false)

      addNotification(
        "success",
        `Group "${groupData.name}" saved successfully with ${groupData.properties.length} properties`,
      )
    } catch (error) {
      console.error('Error creating property group:', error);
      addNotification("error", "Failed to save property group")
    }
  }

  const handleSaveAsPropertySet = async () => {
    if (selectedProperties.length === 0) {
      addNotification("error", "Please select at least one property")
      return
    }

    if (!propertySetName.trim()) {
      addNotification("error", "Please enter a specification name")
      return
    }

    try {
      const propertySetData = {
        name: propertySetName.trim(),
        properties: selectedProperties.map((p) => ({
          property: p.property,
          uom: p.uom,
          basis: p.basis,
          linear: p.linear,
          min: propertyLimits[p.key]?.min || "",
          max: propertyLimits[p.key]?.max || "",
          description: p.description,
          index_formula: p.index_formula,
          reverse_formula: p.reverse_formula,
          source: p.source,
          sourceId: p.sourceId,
        })),
        setMin: propertySetMin, 
        setMax: propertySetMax, 
      }

      console.log('Creating property set:', propertySetData);
      const result = await createPropertySet(propertySetData)
      await fetchPropertySets()
      
      const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      const updatedSets = [...existingSets, {
        ...propertySetData,
        _id: result.data?._id || `local-${Date.now()}`,
        id: result.data?._id || `local-${Date.now()}`,
        createdAt: new Date().toISOString()
      }];
      localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));
      
      window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
        detail: { sets: updatedSets }
      }));
      
      setPropertySetName("")
      setPropertySetMin("")
      setPropertySetMax("")
      setSelectedProperties([])
      setPropertyLimits({})
      setShowGradePropertyPopup(false)

      addNotification("success", "Specification saved successfully")
    } catch (error) {
      console.error('Error creating property set:', error);
      addNotification("error", "Failed to save specification")
    }
  }

  const handleDeletePropertySet = async (setId) => {
    if (window.confirm("Are you sure you want to delete this specification?")) {
      try {
        await deletePropertySet(setId)
        await fetchPropertySets()
        
        const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
        const updatedSets = existingSets.filter(set => set._id !== setId && set.id !== setId);
        localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));
        
        window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
          detail: { sets: updatedSets }
        }));
        
        addNotification("success", "Specification deleted successfully")
      } catch (error) {
        addNotification("error", "Failed to delete specification")
      }
    }
  }


  const handleDuplicatePropertySet = async (propertySet) => {
    try {
      const duplicateData = {
        name: `${propertySet.name} (Copy)`,
        properties: propertySet.properties.map(p => ({
          property: p.property,
          uom: p.uom,
          basis: p.basis,
          linear: p.linear,
          min: p.min || "",
          max: p.max || "",
          description: p.description,
          index_formula: p.index_formula,
          reverse_formula: p.reverse_formula,
          source: p.source,
          sourceId: p.sourceId,
        })),
        setMin: propertySet.setMin || "",
        setMax: propertySet.setMax || "",
      }
      const result = await createPropertySet(duplicateData)
      await fetchPropertySets()
      const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      const updatedSets = [...existingSets, {
        ...duplicateData,
        _id: result.data?._id || `local-${Date.now()}`,
        id: result.data?._id || `local-${Date.now()}`,
        createdAt: new Date().toISOString()
      }];
      localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));
      window.dispatchEvent(new CustomEvent('propertySetsUpdated', { detail: { sets: updatedSets } }));
      addNotification("success", `Specification duplicated as "${duplicateData.name}"`)
    } catch (error) {
      console.error('Error duplicating specification:', error);
      addNotification("error", "Failed to duplicate specification")
    }
  }

  const handleClearAllPropertySets = async () => {
    if (window.confirm("Are you sure you want to delete all specifications? This action cannot be undone.")) {
      try {
        for (const set of savedPropertySets) {
          await deletePropertySet(set._id || set.id)
        }
        await fetchPropertySets()
        
        localStorage.setItem('savedPropertySets', JSON.stringify([]));
        
        window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
          detail: { sets: [] }
        }));
        
        addNotification("info", "All specifications cleared")
      } catch (error) {
        addNotification("error", "Failed to clear specifications")
      }
    }
  }

  const togglePropertySet = (setId) => {
    setExpandedPropertySets((prev) => ({
      ...prev,
      [setId]: !prev[setId],
    }))
  }

  const handleAddPropertyClick = () => {
    setShowAddPropertyPopup(true)
    setEditingIndex(null)
    setNewProperty({
      property: "",
      uom: "",
      basis: "volume",
      linear: true,
      min: "",
      max: "",
      description: "",
      index_formula: "",
      reverse_formula: "",
    })
    setFormErrors({}) 
  }

  const handleSaveProperty = async () => {
    if (!validatePropertyForm()) {
      addNotification("error", "Please fix the form errors before saving")
      return
    }

    const propertyToSave = {
      ...newProperty,
      property: newProperty.property.trim(),
      uom: newProperty.uom.trim(),
      basis: newProperty.basis,
      linear: newProperty.linear,
      // min: newProperty.min,
      // max: newProperty.max,
          min: newProperty.min || "", // Empty string if not provided
    max: newProperty.max || "",
      description: newProperty.description,
      index_formula: newProperty.index_formula,
      reverse_formula: newProperty.reverse_formula,
    }

    try {
      if (editingIndex !== null) {
        const propertyId = globalProperties[editingIndex]._id
        await updateGlobalProperty(propertyId, propertyToSave)
        addNotification("success", "Property updated successfully")
      } else {
        await createGlobalProperty(propertyToSave)
        addNotification("success", "Property added successfully")
      }
      
      await fetchGlobalProperties()
      
      // नई प्रॉपर्टी जोड़ने के बाद पेज नंबर को आखिरी पेज पर सेट करें
      const totalPages = getTotalPages(getCombinedProperties());
      setGlobalPropertiesPage(totalPages);
      
      setShowAddPropertyPopup(false)
      setEditingIndex(null)
      setNewProperty({
        property: "",
        uom: "",
        basis: "volume",
        linear: true,
        min: "",
        max: "",
        description: "",
        index_formula: "",
        reverse_formula: "",
      })
      setFormErrors({}) 
    } catch (error) {
      addNotification("error", "Failed to save property")
    }
  }

  const handleDeleteGlobalProperty = async (index) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        const propertyId = globalProperties[index]._id
        await deleteGlobalProperty(propertyId)
        
        await fetchGlobalProperties()
        
        if (globalPropertiesPage > 1 && globalProperties.length <= (globalPropertiesPage - 1) * itemsPerPage) {
          setGlobalPropertiesPage(globalPropertiesPage - 1)
        }
        
        addNotification("success", "Property deleted successfully")
      } catch (error) {
        addNotification("error", "Failed to delete property")
      }
    }
  }

  const handleClearAllGlobalProperties = async () => {
    if (window.confirm("Are you sure you want to delete all global properties? This action cannot be undone.")) {
      try {
        for (const property of globalProperties) {
          await deleteGlobalProperty(property._id)
        }
        await fetchGlobalProperties()
        setGlobalPropertiesPage(1) 
        addNotification("info", "All global properties cleared")
      } catch (error) {
        addNotification("error", "Failed to clear global properties")
      }
    }
  }

  const handleEditGlobalProperty = (index) => {
    const property = globalProperties[index]
    setEditingIndex(index) 
    setNewProperty({
      property: property.property,
      uom: property.uom,
      basis: property.basis,
      linear: property.linear,
      min: property.min || "",
      max: property.max || "",
      description: property.description || "",
      index_formula: property.index_formula || "",
      reverse_formula: property.reverse_formula || "",
    })
    setFormErrors({})
    setShowAddPropertyPopup(true)
  }

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        await deletePropertyGroup(groupId)
        await fetchPropertyGroups()
        
        const existingGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
        const updatedGroups = existingGroups.filter(group => group._id !== groupId && group.id !== groupId);
        localStorage.setItem('propertyGroups', JSON.stringify(updatedGroups));
        
        window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
          detail: { groups: updatedGroups }
        }));
        
        if (savedGroupsPage > 1 && savedGroups.length <= (savedGroupsPage - 1) * itemsPerPage) {
          setSavedGroupsPage(savedGroupsPage - 1)
        }
        
        addNotification("success", "Group deleted successfully")
      } catch (error) {
        addNotification("error", "Failed to delete property group")
      }
    }
  }

  const handleClearAllGroups = async () => {
    if (window.confirm("Are you sure you want to delete all groups? This action cannot be undone.")) {
      try {
        for (const group of savedGroups) {
          await deletePropertyGroup(group._id || group.id)
        }
        await fetchPropertyGroups()
        
        localStorage.setItem('propertyGroups', JSON.stringify([]));
        
        window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
          detail: { groups: [] }
        }));
        
        setSavedGroupsPage(1)
        addNotification("info", "All groups cleared")
      } catch (error) {
        addNotification("error", "Failed to clear groups")
      }
    }
  }

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  const handlePropertySelect = (property, source, sourceId, index = null) => {
    const propertyKey = `${source}-${sourceId}-${index !== null ? index : property.property}`

    setSelectedProperties((prev) => {
      const isSelected = prev.some((p) => p.key === propertyKey)
      if (isSelected) {
        return prev.filter((p) => p.key !== propertyKey)
      } else {
        return [
          ...prev,
          {
            ...property,
            key: propertyKey,
            source,
            sourceId,
            originalIndex: index,
            sourceName:
              source === "saved"
                ? savedProperties.find((p) => p.id === sourceId)?.customName ||
                  savedProperties.find((p) => p.id === sourceId)?.name
                : source === "group"
                  ? savedGroups.find((g) => g._id === sourceId || g.id === sourceId)?.name
                  : source === "propertySet"
                    ? savedPropertySets.find((s) => s._id === sourceId || s.id === sourceId)?.name
                    : source === "global"
                      ? "Global Properties"
                      : source === "app"
                        ? "App Saved Properties"
                        : "Unknown Source",
          },
        ]
      }
    })
  }

  const isPropertySelected = (property, source, sourceId, index = null) => {
    const key = `${source}-${sourceId}-${index !== null ? index : property.property}`
    return selectedProperties.some((p) => p.key === key)
  }

  const handleSelectAllGlobal = () => {
    const displayProperties = activeSubTab === "global" ? globalProperties : getCombinedProperties();
    if (displayProperties.length === 0) return

    const allSelected = displayProperties.every((prop, index) => isPropertySelected(prop, "global", "global", index))

    if (allSelected) {
      setSelectedProperties((prev) => prev.filter((p) => p.source !== "global"))
    } else {
      displayProperties.forEach((prop, index) => {
        handlePropertySelect(prop, "global", "global", index)
      })
    }
  }

  const handleSelectAllFromGroup = (group) => {
    if (!group.properties || !Array.isArray(group.properties)) return

    const allSelected = group.properties.every((prop, index) => isPropertySelected(prop, "group", group._id || group.id, index))

    if (allSelected) {
      setSelectedProperties((prev) => prev.filter((p) => !(p.source === "group" && p.sourceId === (group._id || group.id))))
    } else {
      group.properties.forEach((prop, index) => {
        handlePropertySelect(prop, "group", group._id || group.id, index)
      })
    }
  }

  const handleSelectAllFromPropertySet = (propertySet) => {
    if (!propertySet.properties || !Array.isArray(propertySet.properties)) return

    const allSelected = propertySet.properties.every((prop, index) =>
      isPropertySelected(prop, "propertySet", propertySet._id || propertySet.id, index),
    )

    if (allSelected) {
      setSelectedProperties((prev) => prev.filter((p) => !(p.source === "propertySet" && p.sourceId === (propertySet._id || propertySet.id))))
    } else {
      propertySet.properties.forEach((prop, index) => {
        handlePropertySelect(prop, "propertySet", propertySet._id || propertySet.id, index)
      })
    }
  }

  const isAllSelectedFromGroup = (group) => {
    if (!group.properties || !Array.isArray(group.properties)) return false
    return group.properties.every((prop, index) => isPropertySelected(prop, "group", group._id || group.id, index))
  }

  const isAllSelectedFromPropertySet = (propertySet) => {
    if (!propertySet.properties || !Array.isArray(propertySet.properties)) return false
    return propertySet.properties.every((prop, index) => isPropertySelected(prop, "propertySet", propertySet._id || propertySet.id, index))
  }

  const isAllGlobalSelected = () => {
    const displayProperties = activeSubTab === "global" ? globalProperties : getCombinedProperties();
    return displayProperties.every((prop, index) => isPropertySelected(prop, "global", "global", index))
  }

  const handleClosePopup = () => {
    setShowAddPropertyPopup(false)
    setEditingIndex(null)
    setNewProperty({
      property: "",
      uom: "",
      basis: "volume",
      linear: true,
      min: "",
      max: "",
      description: "",
      index_formula: "",
      reverse_formula: "",
    })
    setFormErrors({}) 
  }

  const handleCloseEditPopup = () => {
    setShowEditGroupPopup(false)
    setShowEditPropertySetPopup(false)
    setEditingGroup(null)
    setEditingPropertySet(null)
    setGroupProperties([])
    setEditingPropertySetProperties([])
    setEditingPropertySetMin("")
    setEditingPropertySetMax("")
  }

  const handleAddPropertyFromGroups = () => {
    setShowGradePropertyPopup(true)
    setSelectedProperties([])
    setGroupName("")
    setPropertySetName("")
    setPropertySetMin("")
    setPropertySetMax("")
  }

  const handleCloseGradePropertyPopup = () => {
    setShowGradePropertyPopup(false)
    setSelectedProperties([])
    setGroupName("")
    setPropertySetName("")
    setPropertySetMin("")
    setPropertySetMax("")
  }

  const handlePropertyChange = (field, value) => {
    const v = field === "property" ? normalizePropertyName(value) : value;
    setNewProperty((prev) => ({
      ...prev,
      [field]: v,
    }))

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }))
    }

    if (field === "linear" && value === true) {
      setFormErrors(prev => ({
        ...prev,
        index_formula: "",
        reverse_formula: ""
      }))
    }
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const downloadGlobalCSV = () => {
    setIsExporting(true);
    try {
      const exportData = activeSubTab === "global" ? globalProperties : getCombinedProperties();
      const headers = ["Property", "UOM", "Basis", "Blending Type", "Min", "Max", "Description", "Index Formula", "Reverse Formula", "Source"]
      const escapeCSV = (val) => {
        const s = String(val ?? "").replace(/"/g, '""');
        return `"${s}"`;
      };
      const rows = exportData.map((prop) => [
        escapeCSV(prop.property),
        escapeCSV(prop.uom),
        escapeCSV(prop.basis),
        prop.linear ? "Linear" : "Non-Linear",
        escapeCSV(prop.min),
        escapeCSV(prop.max),
        escapeCSV(prop.description),
        escapeCSV(prop.index_formula),
        escapeCSV(prop.reverse_formula),
        prop.isFromApp ? "App" : prop.isFromReport ? "Report" : "Global"
      ])

      const BOM = "\uFEFF";
      const csvContent = BOM + [headers.join(","), ...rows.map((row) => row.join(","))].join("\n")

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `properties_export_${new Date().toISOString().slice(0,10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      addNotification("success", "Properties exported successfully")
    } catch (error) {
      addNotification("error", "Export failed — please try again")
    } finally {
      setIsExporting(false);
    }
  }

  /* ─── Filtering & Sorting Helpers ─── */
  const applyGlobalFilters = (data) => {
    let filtered = [...data];
    if (globalSearch.trim()) {
      const q = globalSearch.toLowerCase();
      filtered = filtered.filter(p =>
        (p.property || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (p.uom || "").toLowerCase().includes(q)
      );
    }
    if (globalBasisFilter !== "all") {
      filtered = filtered.filter(p => (p.basis || "").toLowerCase() === globalBasisFilter);
    }
    if (globalTypeFilter !== "all") {
      const isLinear = globalTypeFilter === "linear";
      filtered = filtered.filter(p => p.linear === isLinear);
    }
    filtered.sort((a, b) => {
      if (globalSort === "name-asc") return (a.property || "").localeCompare(b.property || "");
      if (globalSort === "name-desc") return (b.property || "").localeCompare(a.property || "");
      if (globalSort === "updated") return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
      return 0;
    });
    return filtered;
  };

  const applyGroupFilters = (data) => {
    let filtered = [...data];
    if (groupSearch.trim()) {
      const q = groupSearch.toLowerCase();
      filtered = filtered.filter(g => (g.name || "").toLowerCase().includes(q));
    }
    if (groupFilter === "empty") filtered = filtered.filter(g => !g.properties || g.properties.length === 0);
    if (groupFilter === "non-empty") filtered = filtered.filter(g => g.properties && g.properties.length > 0);
    filtered.sort((a, b) => {
      if (groupSort === "name-asc") return (a.name || "").localeCompare(b.name || "");
      if (groupSort === "name-desc") return (b.name || "").localeCompare(a.name || "");
      if (groupSort === "updated") return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      return 0;
    });
    return filtered;
  };

  const applySpecFilters = (data) => {
    let filtered = [...data];
    if (specSearch.trim()) {
      const q = specSearch.toLowerCase();
      filtered = filtered.filter(s => (s.name || "").toLowerCase().includes(q));
    }
    if (specFilter === "empty") filtered = filtered.filter(s => !s.properties || s.properties.length === 0);
    if (specFilter === "non-empty") filtered = filtered.filter(s => s.properties && s.properties.length > 0);
    filtered.sort((a, b) => {
      if (specSort === "name-asc") return (a.name || "").localeCompare(b.name || "");
      if (specSort === "name-desc") return (b.name || "").localeCompare(a.name || "");
      if (specSort === "updated") return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      return 0;
    });
    return filtered;
  };

  const displayProperties = applyGlobalFilters(activeSubTab === "global" ? globalProperties : getCombinedProperties());

  const paginatedDisplayProperties = getPaginatedData(displayProperties, globalPropertiesPage);
  const totalDisplayPages = getTotalPages(displayProperties);

  const filteredSavedGroups = applyGroupFilters(savedGroups);
  const filteredSavedPropertySets = applySpecFilters(savedPropertySets);

  const paginatedSavedGroups = getPaginatedData(filteredSavedGroups, savedGroupsPage)
  const paginatedSavedPropertySets = getPaginatedData(filteredSavedPropertySets, savedPropertySetsPage)

  const totalGroupsPages = getTotalPages(filteredSavedGroups)
  const totalPropertySetsPages = getTotalPages(filteredSavedPropertySets)

  if (!currentUser) {
    return <Login onLogin={() => {}} users={[]} setUsers={() => {}} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <NotificationToaster notifications={notifications} />

      {/* Common Logout Confirmation Modal */}
      <LogoutConfirmModal 
        isOpen={showLogoutConfirm}
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />

      {/* Add Property Popup — Professional Design */}
      {showAddPropertyPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-600 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {editingIndex !== null ? "Edit Property" : "Add New Property"}
                  </h2>
                </div>
              </div>
              <button onClick={handleClosePopup} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {/* Section 1: Identity */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Property Identity</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Property Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newProperty.property}
                      onChange={(e) => handlePropertyChange("property", e.target.value)}
                      placeholder="Property Name"
                      className={`w-full border rounded-xl px-3.5 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-sm placeholder-gray-400 ${
                        formErrors.property ? "border-red-300 bg-red-50" : "border-gray-300"
                      }`}
                    />
                    {formErrors.property && <p className="mt-1 text-xs text-red-600">{formErrors.property}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Unit of Measure (UOM)
                    </label>
                    <input
                      type="text"
                      value={newProperty.uom}
                      onChange={(e) => handlePropertyChange("uom", e.target.value)}
                      placeholder="UOM"
                      className={`w-full border rounded-xl px-3.5 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-sm placeholder-gray-400 ${
                        formErrors.uom ? "border-red-300 bg-red-50" : "border-gray-300"
                      }`}
                    />
                    {formErrors.uom && <p className="mt-1 text-xs text-red-600">{formErrors.uom}</p>}
                  </div>
                </div>
              </div>

              {/* Section 2: Blending Behavior */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Blending Behavior</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Blending Basis <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <select
                        value={newProperty.basis}
                        onChange={(e) => handlePropertyChange("basis", e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-3.5 pr-10 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white text-sm appearance-none"
                      >
                        {basisOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Blending Type <span className="text-red-500">*</span></label>
                    <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                      <button
                        type="button"
                        onClick={() => handlePropertyChange("linear", true)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                          newProperty.linear === true || newProperty.linear === "true"
                            ? 'bg-white text-green-700 shadow-sm border border-green-200'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l5-5 5 5" />
                        </svg>
                        Linear
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePropertyChange("linear", false)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                          newProperty.linear === false || newProperty.linear === "false"
                            ? 'bg-white text-purple-700 shadow-sm border border-purple-200'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17c2-4 5-10 10-10" />
                        </svg>
                        Non-Linear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Non-linear formulas section */}
              {(newProperty.linear === false || newProperty.linear === "false") && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 7a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-amber-800">Non-Linear Blending Formulas</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Index Formula (value → index) <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={newProperty.index_formula}
                      onChange={(e) => handlePropertyChange("index_formula", e.target.value)}
                      placeholder="e.g., math.log10(x), x**2, math.sqrt(x)"
                      className={`w-full border rounded-xl px-3.5 py-2.5 font-mono text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-white ${
                        formErrors.index_formula ? "border-red-300" : "border-gray-300"
                      }`}
                    />
                    {formErrors.index_formula ? (
                      <p className="mt-1 text-xs text-red-600">{formErrors.index_formula}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Reverse Formula (index → value) <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={newProperty.reverse_formula}
                      onChange={(e) => handlePropertyChange("reverse_formula", e.target.value)}
                      placeholder="e.g., 10**x, math.sqrt(x), x**(1/2)"
                      className={`w-full border rounded-xl px-3.5 py-2.5 font-mono text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-white ${
                        formErrors.reverse_formula ? "border-red-300" : "border-gray-300"
                      }`}
                    />
                    {formErrors.reverse_formula ? (
                      <p className="mt-1 text-xs text-red-600">{formErrors.reverse_formula}</p>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Section 3: Description */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Documentation</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <textarea
                    value={newProperty.description}
                    onChange={(e) => handlePropertyChange("description", e.target.value)}
                    placeholder="Optional description..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all placeholder-gray-400 resize-none"
                  />
                </div>
              </div>

              {/* Summary card */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Property</div>
                    <div className="text-sm font-semibold text-gray-900 truncate">{newProperty.property || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Basis</div>
                    <div className="text-sm font-semibold text-gray-900">{newProperty.basis || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Type</div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      (newProperty.linear === true || newProperty.linear === "true") ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {(newProperty.linear === true || newProperty.linear === "true") ? 'Linear' : 'Non-Linear'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <button
                onClick={handleClosePopup}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProperty}
                disabled={Object.keys(formErrors).length > 0}
                className={`px-5 py-2 rounded-xl font-medium flex items-center gap-2 text-sm transition-all ${
                  Object.keys(formErrors).length > 0
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-purple-600 text-white hover:bg-purple-700 shadow-sm"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {editingIndex !== null ? "Update Property" : "Save Property"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Group Popup */}
      {showEditGroupPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-600 flex items-center justify-center">
                  <IconEdit className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Edit Group: {editingGroup?.name}</h2>
                  <p className="text-xs text-gray-500">Add or remove properties from this group</p>
                </div>
              </div>
              <button onClick={handleCloseEditPopup} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <IconX className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Properties</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {displayProperties.map((property, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{property.property}</div>
                          <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                        </div>
                        <button
                          onClick={() => handleAddPropertyToGroup(property)}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap ml-2"
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Group Properties
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {groupProperties.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No properties in group</p>
                    ) : (
                      groupProperties.map((property, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{property.property}</div>
                            <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                          </div>
                          <button
                            onClick={() => handleRemovePropertyFromGroup(index)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <BtnSecondary onClick={handleCloseEditPopup}>Cancel</BtnSecondary>
              <BtnPrimary onClick={handleSaveGroupEdit} color="orange">
                <IconCheck /> Save Changes
              </BtnPrimary>
            </div>
          </div>
        </div>
      )}

      {/* Edit Property Set Popup */}
      {showEditPropertySetPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
                  <IconEdit className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Edit Specification: {editingPropertySet?.name}</h2>
                  <p className="text-xs text-gray-500">Manage properties and set min/max limits</p>
                </div>
              </div>
              <button onClick={handleCloseEditPopup} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <IconX className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Properties</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {displayProperties.map((property, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{property.property}</div>
                          <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                        </div>
                        <button
                          onClick={() => handleAddPropertyToPropertySet(property)}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap ml-2"
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Specification Properties
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {editingPropertySetProperties.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No properties in set</p>
                    ) : (
                      editingPropertySetProperties.map((property, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{property.property}</div>
                            <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                            <div className="flex gap-2 mt-2">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">Min</label>
                                <input
                                  type="number"
                                  value={property.min || ""}
                                  onChange={(e) => handlePropertyMinMaxChange(index, "min", e.target.value)}
                                  placeholder="Min"
                                  className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent ${
                                    property.min && property.max && Number(property.min) > Number(property.max) ? "border-red-400 bg-red-50" : !property.min ? "border-amber-300" : "border-gray-300"
                                  }`}
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">Max</label>
                                <input
                                  type="number"
                                  value={property.max || ""}
                                  onChange={(e) => handlePropertyMinMaxChange(index, "max", e.target.value)}
                                  placeholder="Max"
                                  className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent ${
                                    property.min && property.max && Number(property.min) > Number(property.max) ? "border-red-400 bg-red-50" : !property.max ? "border-amber-300" : "border-gray-300"
                                  }`}
                                />
                              </div>
                            </div>
                            {property.min && property.max && Number(property.min) > Number(property.max) && (
                              <p className="text-xs text-red-500 mt-1">Min must be ≤ Max</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemovePropertyFromPropertySet(index)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <BtnSecondary onClick={handleCloseEditPopup}>Cancel</BtnSecondary>
              <BtnPrimary onClick={handleSavePropertySetEdit} color="indigo">
                <IconCheck /> Save Changes
              </BtnPrimary>
            </div>
          </div>
        </div>
      )}

      {/* Create Property Group / Specification Popup */}
      {showGradePropertyPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl border border-gray-100 max-w-3xl w-full max-h-[95vh] overflow-hidden my-4">
            {/* Header */}
            <div className={`px-4 sm:px-6 py-4 border-b ${
              activeSubTab === "groups"
                ? "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"
                : "bg-gradient-to-r from-indigo-50 to-slate-50 border-indigo-200"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-sm ${
                    activeSubTab === "groups" ? "bg-emerald-600" : "bg-indigo-600"
                  }`}>
                    {activeSubTab === "groups" ? (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                      {activeSubTab === "groups" ? "Create Property Group" : "Create Property Specification"}
                    </h2>
                    <p className="text-gray-500 text-xs sm:text-sm">
                      {activeSubTab === "groups"
                        ? "Select properties to create a reusable group"
                        : "Name, select properties, and set specification limits"}
                    </p>
                  </div>
                </div>
                <button onClick={handleCloseGradePropertyPopup} className="p-2 hover:bg-white/60 rounded-lg transition-colors">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-80px)]">

              {/* STEP 1 — Name */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  {activeSubTab === "groups" ? "Group Name *" : "Specification Name *"}
                </label>
                <input
                  type="text"
                  value={activeSubTab === "groups" ? groupName : propertySetName}
                  onChange={(e) => activeSubTab === "groups" ? setGroupName(e.target.value) : setPropertySetName(e.target.value)}
                  placeholder={activeSubTab === "groups" ? "e.g. Gasoline Properties" : "e.g. Euro V Gasoline Spec"}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:border-transparent transition-colors ${
                    activeSubTab === "groups" ? "border-emerald-300 focus:ring-emerald-500" : "border-indigo-300 focus:ring-indigo-500"
                  }`}
                />
              </div>

              {/* STEP 2 — Select Properties (single unified list) */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
                <div className={`px-4 py-3 border-b flex items-center justify-between ${
                  activeSubTab === "groups" ? "bg-emerald-50 border-emerald-100" : "bg-indigo-50 border-indigo-100"
                }`}>
                  <div className="flex items-center gap-2">
                    <svg className={`w-4 h-4 ${activeSubTab === "groups" ? "text-emerald-600" : "text-indigo-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-900">Properties</h3>
                  </div>
                  <button
                    onClick={handleSelectAllGlobal}
                    className={`px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-colors ${
                      activeSubTab === "groups" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                  >
                    {isAllGlobalSelected() ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="p-3 sm:p-4">
                  {displayProperties.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {displayProperties.map((property, index) => (
                        <div
                          key={property._id || property.id || index}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            isPropertySelected(property, "global", "global", index)
                              ? "border-green-500 bg-green-50 shadow-sm"
                              : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                          }`}
                          onClick={() => handlePropertySelect(property, "global", "global", index)}
                        >
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isPropertySelected(property, "global", "global", index)}
                              onChange={() => handlePropertySelect(property, "global", "global", index)}
                              className="w-4 h-4 text-green-600 rounded focus:ring-green-500 mt-0.5 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-gray-900 text-sm truncate">{property.property}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ml-1 ${
                                  property.linear ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                                }`}>
                                  {property.linear ? "Linear" : "Non-Linear"}
                                </span>
                              </div>
                              <div className="flex gap-3 text-xs text-gray-500">
                                <span>UOM: {property.uom || "\u2014"}</span>
                                <span className="capitalize">Basis: {property.basis || "\u2014"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-400 text-sm">No properties available. Create global properties first.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* STEP 3 — Spec Limits (only for specifications, only when properties selected) */}
              {activeSubTab === "propertySets" && selectedProperties.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
                  <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-900">Specification Limits</h3>
                    <span className="text-xs text-gray-400 ml-auto">Leave blank for no constraint</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Property</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">UOM</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase w-28">Min</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase w-28">Max</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedProperties.map((prop) => (
                          <tr key={prop.key} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-medium text-gray-900">{prop.property}</td>
                            <td className="px-4 py-2.5 text-gray-500">{prop.uom || "\u2014"}</td>
                            <td className="px-4 py-2">
                              <input type="number" step="any"
                                value={propertyLimits[prop.key]?.min ?? ""}
                                onChange={(e) => setPropertyLimits(prev => ({ ...prev, [prop.key]: { ...prev[prop.key], min: e.target.value } }))}
                                placeholder="\u2014"
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input type="number" step="any"
                                value={propertyLimits[prop.key]?.max ?? ""}
                                onChange={(e) => setPropertyLimits(prev => ({ ...prev, [prop.key]: { ...prev[prop.key], max: e.target.value } }))}
                                placeholder="\u2014"
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-2 py-2 text-center">
                              <button onClick={() => handlePropertySelect(prop, prop.source, prop.sourceId, prop.originalIndex)}
                                className="text-gray-400 hover:text-red-500 transition-colors" title="Remove">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
                <BtnPrimary
                  onClick={activeSubTab === "groups" ? handleSaveAsGroup : handleSaveAsPropertySet}
                  disabled={((activeSubTab === "groups" ? !groupName.trim() : !propertySetName.trim()) || selectedProperties.length === 0)}
                  color={activeSubTab === "groups" ? "emerald" : "indigo"}
                >
                  <IconCheck />
                  {activeSubTab === "groups" ? "Save Group" : "Save Specification"}
                </BtnPrimary>
                <BtnSecondary onClick={handleCloseGradePropertyPopup}>
                  Cancel
                </BtnSecondary>
                {selectedProperties.length === 0 && (
                  <span className="text-amber-600 text-xs flex items-center gap-1 ml-2">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Select at least one property
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Navigation
        currentUser={currentUser}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onLogout={handleLogoutClick}
        onShowUserManagement={onShowUserManagement}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={onToggleSidebar}
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-0"}`}>
        <Header currentUser={currentUser} onTestAPI={() => {}} onToggleSidebar={onToggleSidebar} />

        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto w-full">

            <div className="mb-4 sm:mb-6">
              <div className="bg-white rounded-xl p-1 shadow-lg border border-gray-200 inline-flex flex-wrap gap-1">
                <button
                  onClick={() => setActiveSubTab("global")}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                    activeSubTab === "global"
                      ? "bg-purple-600 text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Global Properties
                </button>
                <button
                  onClick={() => setActiveSubTab("groups")}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                    activeSubTab === "groups"
                      ? "bg-orange-600 text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Property Groups
                </button>
                <button
                  onClick={() => setActiveSubTab("propertySets")}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                    activeSubTab === "propertySets"
                      ? "bg-indigo-600 text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Property Specifications
                </button>
              </div>
            </div>

            <div className="mb-4 sm:mb-6 md:mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                    {activeSubTab === "global"
                      ? "Global Properties"
                      : activeSubTab === "groups"
                        ? "Property Groups"
                        : "Property Sets"}
                  </h1>
                  <p className="text-gray-600 mt-1 sm:mt-2 text-xs sm:text-sm md:text-base">
                    {activeSubTab === "global"
                      ? "Global Properties Only"
                      : activeSubTab === "groups"
                        ? "View and manage your property groups"
                        : "View and manage your property specifications"}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {activeSubTab === "global" ? (
                    <>
                      <BtnPrimary onClick={handleAddPropertyClick} color="purple">
                        <IconPlus /> Add Property
                      </BtnPrimary>
                      {globalProperties.length > 0 && (
                        <>
                          <BtnSecondary onClick={downloadGlobalCSV} disabled={isExporting}>
                            {isExporting ? <IconSpinner /> : <IconDownload />}
                            {isExporting ? "Exporting…" : "Export CSV"}
                          </BtnSecondary>
                          <BtnDanger onClick={handleClearAllGlobalProperties}>
                            <IconTrash /> Clear All
                          </BtnDanger>
                        </>
                      )}
                    </>
                  ) : activeSubTab === "groups" ? (
                    <>
                      <BtnPrimary onClick={handleAddPropertyFromGroups} color="orange">
                        <IconPlus /> Create Group
                      </BtnPrimary>
                      {savedGroups.length > 0 && (
                        <BtnDanger onClick={handleClearAllGroups}>
                          <IconTrash /> Clear All
                        </BtnDanger>
                      )}
                    </>
                  ) : activeSubTab === "propertySets" ? (
                    <>
                      <BtnPrimary onClick={handleAddPropertyFromGroups} color="indigo">
                        <IconPlus /> Create Specification
                      </BtnPrimary>
                      {savedPropertySets.length > 0 && (
                        <BtnDanger onClick={handleClearAllPropertySets}>
                          <IconTrash /> Clear All
                        </BtnDanger>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            {activeSubTab === "global" && (
              <div className="space-y-4">
                {/* Controls Row */}
                <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                  <div className="relative flex-1 min-w-[180px]">
                    <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={globalSearch} onChange={(e) => { setGlobalSearch(e.target.value); setGlobalPropertiesPage(1); }}
                      placeholder="Search properties…" className="w-full h-9 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>
                  <select value={globalBasisFilter} onChange={(e) => { setGlobalBasisFilter(e.target.value); setGlobalPropertiesPage(1); }}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
                    <option value="all">Basis: All</option>
                    <option value="volume">Volume</option>
                    <option value="mass">Mass</option>
                  </select>
                  <select value={globalTypeFilter} onChange={(e) => { setGlobalTypeFilter(e.target.value); setGlobalPropertiesPage(1); }}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
                    <option value="all">Type: All</option>
                    <option value="linear">Linear</option>
                    <option value="non-linear">Non-linear</option>
                  </select>
                  <select value={globalSort} onChange={(e) => setGlobalSort(e.target.value)}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
                    <option value="name-asc">Name A→Z</option>
                    <option value="name-desc">Name Z→A</option>
                    <option value="updated">Updated</option>
                  </select>
                </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="px-3 sm:px-4 md:px-6 py-4 bg-gradient-to-r from-purple-100 to-purple-100 border-b border-purple-200">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Global Properties</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                            Global Properties Only
                          </span>
                          {hasAutoSaved && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                              Auto-saved to Database
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isLoading.global ? (
                  <LoadingSpinner 
                    size="large" 
                    text="Loading global properties..." 
                  />
                ) : displayProperties.length === 0 ? (
                  <div className="p-6 sm:p-8 text-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No properties yet</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Start by adding your first property</p>
                    <button
                      onClick={handleAddPropertyClick}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm sm:text-base"
                    >
                      Add Property
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="min-w-full text-sm">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Property</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">UOM</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              <span className="inline-flex items-center gap-1" title="Basis controls blending weights (mass vs volume) and must match property definition.">
                                Basis
                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              </span>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Blending Type</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {paginatedDisplayProperties.map((property, index) => (
                            <tr key={property._id || property.id || index} className={`hover:bg-blue-50/40 transition-colors ${index % 2 === 1 ? "bg-gray-50/50" : ""}`}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-bold text-purple-600">{property.property?.charAt(0)?.toUpperCase() || 'P'}</span>
                                  </div>
                                  <span className="font-semibold text-gray-900">{property.property}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-mono font-medium">
                                  {property.uom || "NA"}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  property.basis === 'volume' ? 'bg-blue-100 text-blue-700' :
                                  property.basis === 'mass' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {property.basis ? property.basis.charAt(0).toUpperCase() + property.basis.slice(1) : "—"}
                                </span>
                              </td>
                              <td className="px-4 py-3 hidden md:table-cell max-w-[200px]">
                                <span className="text-gray-500 text-xs line-clamp-2 block" title={property.description || ""}>{property.description || <span className="text-gray-300">—</span>}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  property.linear ? "bg-green-100 text-green-700 border border-green-200" : "bg-purple-100 text-purple-700 border border-purple-200"
                                }`}>
                                  {property.linear ? (
                                    <><svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M7 12l5-5 5 5" /></svg>Linear</>
                                  ) : (
                                    <><span className="font-mono text-[10px] font-bold italic mr-0.5">fx</span>Non-Linear</>
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {property._id && (
                                  <div className="flex justify-end gap-1">
                                    <button
                                      onClick={() => {
                                        const globalIndex = globalProperties.findIndex(p => p._id === property._id);
                                        if (globalIndex !== -1) handleEditGlobalProperty(globalIndex);
                                      }}
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all" title="Edit"
                                    >
                                      <IconEdit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        const globalIndex = globalProperties.findIndex(p => p._id === property._id);
                                        if (globalIndex !== -1) handleDeleteGlobalProperty(globalIndex);
                                      }}
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete"
                                    >
                                      <IconTrash className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {renderPagination(
                      globalPropertiesPage,
                      totalDisplayPages,
                      setGlobalPropertiesPage,
                      displayProperties.length,
                    )}
                  </>
                )}
              </div>
              </div>
            )}

            {/* Groups Tab */}
            {activeSubTab === "groups" && (
              <div className="space-y-4 sm:space-y-6">
                {/* Controls Row */}
                <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                  <div className="relative flex-1 min-w-[180px]">
                    <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={groupSearch} onChange={(e) => { setGroupSearch(e.target.value); setSavedGroupsPage(1); }}
                      placeholder="Search groups…" className="w-full h-9 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>
                  <select value={groupFilter} onChange={(e) => { setGroupFilter(e.target.value); setSavedGroupsPage(1); }}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-orange-500">
                    <option value="all">All Groups</option>
                    <option value="empty">Empty</option>
                    <option value="non-empty">Non-empty</option>
                  </select>
                  <select value={groupSort} onChange={(e) => setGroupSort(e.target.value)}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-orange-500">
                    <option value="name-asc">Name A→Z</option>
                    <option value="name-desc">Name Z→A</option>
                    <option value="updated">Updated</option>
                  </select>
                </div>

                {isLoading.groups ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <LoadingSpinner 
                      size="large" 
                      text="Loading property groups..." 
                    />
                  </div>
                ) : savedGroups.length === 0 ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No property groups yet</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Create your first property group by clicking "Create Group"</p>
                    <button
                      onClick={handleAddPropertyFromGroups}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors text-sm sm:text-base"
                    >
                      Create First Group
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {paginatedSavedGroups.map((group) => (
                      <div
                        key={group._id}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                      >
                        <div
                          className="px-4 sm:px-6 py-4 bg-gradient-to-r from-orange-100 to-orange-100 border-b border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors"
                          onClick={() => toggleGroup(group._id)}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`transform transition-transform ${expandedGroups[group._id] ? "rotate-90" : ""}`}
                              >
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-base sm:text-lg font-bold text-gray-900">{group.name}</h3>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newName = prompt("Enter new group name:", group.name);
                                      if (newName && newName.trim() !== "" && newName !== group.name) {
                                        handleUpdateGroupName(group._id, newName.trim());
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="text-gray-500 text-xs">{formatDate(group.createdAt || group.timestamp)}</span>
                                  <span className="text-gray-500 text-xs">
                                    Click to {expandedGroups[group._id] ? "collapse" : "expand"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 lg:mt-0" onClick={(e) => e.stopPropagation()}>
                              <BtnPrimary onClick={() => handleEditGroup(group)} color="blue" className="h-9 px-3 text-xs">
                                <IconEdit className="w-3.5 h-3.5" /> Edit
                              </BtnPrimary>
                              <BtnDanger onClick={() => handleDeleteGroup(group._id)} className="h-9 px-3 text-xs">
                                <IconTrash className="w-3.5 h-3.5" /> Delete
                              </BtnDanger>
                            </div>
                          </div>
                        </div>

                        {expandedGroups[group._id] && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Property
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    UOM
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Basis
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Blending Type
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {group.properties.map((property, index) => (
                                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="font-medium text-gray-900 text-sm">{property.property}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.uom || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm capitalize">{property.basis || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4">
                                      <span className="text-gray-900 text-sm line-clamp-2">{property.description || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap text-center">
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          property.linear ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {property.linear ? "Linear" : "Non-Linear"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                    {renderPagination(savedGroupsPage, totalGroupsPages, setSavedGroupsPage, filteredSavedGroups.length)}
                  </div>
                )}
              </div>
            )}

            {/* Property Specifications Tab */}
            {activeSubTab === "propertySets" && (
              <div className="space-y-4 sm:space-y-6">
                {/* Controls Row */}
                <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                  <div className="relative flex-1 min-w-[180px]">
                    <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={specSearch} onChange={(e) => { setSpecSearch(e.target.value); setSavedPropertySetsPage(1); }}
                      placeholder="Search specifications…" className="w-full h-9 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                  </div>
                  <select value={specFilter} onChange={(e) => { setSpecFilter(e.target.value); setSavedPropertySetsPage(1); }}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500">
                    <option value="all">All Specs</option>
                    <option value="empty">Empty</option>
                    <option value="non-empty">Non-empty</option>
                  </select>
                  <select value={specSort} onChange={(e) => setSpecSort(e.target.value)}
                    className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500">
                    <option value="name-asc">Name A→Z</option>
                    <option value="name-desc">Name Z→A</option>
                    <option value="updated">Updated</option>
                  </select>
                </div>

                {isLoading.propertySets ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <LoadingSpinner 
                      size="large" 
                      text="Loading specifications..." 
                    />
                  </div>
                ) : savedPropertySets.length === 0 ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No specifications yet</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Create your first specification by clicking "Create Specification"</p>
                    <button
                      onClick={handleAddPropertyFromGroups}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors text-sm sm:text-base"
                    >
                      Create First Set
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {paginatedSavedPropertySets.map((propertySet) => (
                      <div
                        key={propertySet._id}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                      >
                        <div
                          className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-indigo-200 cursor-pointer hover:bg-indigo-200 transition-colors"
                          onClick={() => togglePropertySet(propertySet._id)}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`transform transition-transform ${expandedPropertySets[propertySet._id] ? "rotate-90" : ""}`}
                              >
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-base sm:text-lg font-bold text-gray-900">{propertySet.name}</h3>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newName = prompt("Enter new specification name:", propertySet.name);
                                      if (newName && newName.trim() !== "" && newName !== propertySet.name) {
                                        handleUpdatePropertySetName(propertySet._id, newName.trim());
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  {(propertySet.setMin || propertySet.setMax) && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                      Min: {propertySet.setMin || "—"} / Max: {propertySet.setMax || "—"}
                                    </span>
                                  )}
                                  <span className="text-gray-500 text-xs">{formatDate(propertySet.createdAt || propertySet.timestamp)}</span>
                                  <span className="text-gray-500 text-xs">
                                    Click to {expandedPropertySets[propertySet._id] ? "collapse" : "expand"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 lg:mt-0" onClick={(e) => e.stopPropagation()}>
                              <BtnSecondary onClick={() => handleDuplicatePropertySet(propertySet)} className="h-9 px-3 text-xs" title="Duplicate this specification">
                                <IconCopy className="w-3.5 h-3.5" /> Duplicate
                              </BtnSecondary>
                              <BtnPrimary onClick={() => handleEditPropertySet(propertySet)} color="blue" className="h-9 px-3 text-xs">
                                <IconEdit className="w-3.5 h-3.5" /> Edit
                              </BtnPrimary>
                              <BtnDanger onClick={() => handleDeletePropertySet(propertySet._id)} className="h-9 px-3 text-xs">
                                <IconTrash className="w-3.5 h-3.5" /> Delete
                              </BtnDanger>
                            </div>
                          </div>
                        </div>

                        {expandedPropertySets[propertySet._id] && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Property
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    UOM
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Basis
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Min
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Max
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Blending Type
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {propertySet.properties.map((property, index) => (
                                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="font-medium text-gray-900 text-sm">{property.property}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.uom || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm capitalize">{property.basis || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.min || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.max || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4">
                                      <span className="text-gray-900 text-sm line-clamp-2">{property.description || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap text-center">
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          property.linear ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {property.linear ? "Linear" : "Non-Linear"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                    {renderPagination(savedPropertySetsPage, totalPropertySetsPages, setSavedPropertySetsPage, filteredSavedPropertySets.length)}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default PropertiesPage